import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Plus, X, AlertCircle, CheckCircle, FileText, Trash2 } from 'lucide-react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { formatDate, getStatusColor, getStatusLabel } from '../lib/utils';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { cn } from '../lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasRole, user } = useAuth();
  const { theme } = useTheme();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const isDark = theme === 'dark';
  
  const [newDegat, setNewDegat] = useState({
    description: '',
    constat_fait: false,
    cout_estime: ''
  });

  useEffect(() => {
    fetchVehicle();
  }, [id]);

  const fetchVehicle = async () => {
    try {
      const response = await api.get(`/vehicles/${id}`);
      setVehicle(response.data);
    } catch (error) {
      console.error('Error fetching vehicle:', error);
      toast.error('Erreur lors du chargement du véhicule');
    } finally {
      setLoading(false);
    }
  };

  const handleAddDegat = async (e) => {
    e.preventDefault();
    try {
      const degat = {
        id: Date.now().toString(),
        date: new Date().toISOString(),
        description: newDegat.description,
        constat_fait: newDegat.constat_fait,
        cout_estime: newDegat.cout_estime ? parseFloat(newDegat.cout_estime) : null,
        reparation_effectuee: false,
        created_by: user.id,
        created_by_nom: `${user.prenom} ${user.nom}`,
        photos: []
      };
      
      const updatedDegats = [...(vehicle.degats || []), degat];
      
      await api.put(`/vehicles/${id}`, { degats: updatedDegats });
      toast.success('Dégât enregistré avec succès');
      setDialogOpen(false);
      setNewDegat({ description: '', constat_fait: false, cout_estime: '' });
      fetchVehicle();
    } catch (error) {
      console.error('Error adding degat:', error);
      toast.error('Erreur lors de l\'ajout du dégât');
    }
  };

  const toggleReparation = async (degatId) => {
    try {
      const updatedDegats = vehicle.degats.map(d => 
        d.id === degatId 
          ? { 
              ...d, 
              reparation_effectuee: !d.reparation_effectuee,
              date_reparation: !d.reparation_effectuee ? new Date().toISOString() : null
            } 
          : d
      );
      
      await api.put(`/vehicles/${id}`, { degats: updatedDegats });
      toast.success('Statut de réparation mis à jour');
      fetchVehicle();
    } catch (error) {
      console.error('Error updating reparation:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteVehicle = async () => {
    if (!window.confirm(`Supprimer définitivement le véhicule ${vehicle.immatriculation} ?`)) return;
    try {
      await api.delete(`/vehicles/${id}`);
      toast.success('Véhicule supprimé');
      navigate('/vehicles');
    } catch (error) {
      console.error('Error deleting vehicle:', error);
      const errorMsg = error?.response?.data?.detail;
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className={cn(
          "animate-spin rounded-full h-12 w-12 border-b-2",
          isDark ? "border-red-500" : "border-slate-900"
        )}></div>
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="text-center py-12">
        <p className={isDark ? "text-zinc-400" : "text-slate-500"}>Véhicule non trouvé</p>
      </div>
    );
  }

  const degatsActifs = vehicle.degats?.filter(d => !d.reparation_effectuee) || [];
  const degatsRepares = vehicle.degats?.filter(d => d.reparation_effectuee) || [];

  return (
    <div className="space-y-6" data-testid="vehicle-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => navigate('/vehicles')}
            data-testid="back-button"
            className={isDark ? "bg-zinc-800 border-white/10 text-white hover:bg-zinc-700" : ""}
          >
            <ArrowLeft size={18} className="mr-2" />
            Retour
          </Button>
          <div>
            <h2 className={cn(
              "text-2xl font-semibold",
              isDark ? "text-white" : "text-slate-900"
            )}>{vehicle.immatriculation}</h2>
            <p className="text-red-500">{vehicle.marque} {vehicle.modele}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`status-badge ${getStatusColor(vehicle.statut)}`}>
            {getStatusLabel(vehicle.statut)}
          </span>
          {hasRole(['admin', 'responsable']) && (
            <Button
              variant="outline"
              onClick={handleDeleteVehicle}
              className="text-red-500 hover:text-red-400 hover:bg-red-500/10 border-red-500/30"
              data-testid="delete-vehicle-button"
            >
              <Trash2 size={18} className="mr-2" />
              Supprimer
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Informations générales */}
        <div className={cn(
          "rounded-xl border p-6",
          isDark ? "bg-zinc-900/50 border-white/10" : "bg-white border-slate-200 shadow-sm"
        )}>
          <h3 className={cn(
            "text-lg font-semibold mb-4",
            isDark ? "text-white" : "text-slate-900"
          )}>Informations générales</h3>
          <div className="space-y-3 text-sm">
            <div>
              <span className={cn(
                "font-medium",
                isDark ? "text-zinc-400" : "text-slate-600"
              )}>Immatriculation:</span>
              <p className={cn(
                "font-mono text-lg",
                isDark ? "text-white" : "text-slate-900"
              )}>{vehicle.immatriculation}</p>
            </div>
            <div>
              <span className={cn(
                "font-medium",
                isDark ? "text-zinc-400" : "text-slate-600"
              )}>Marque/Modèle:</span>
              <p className={isDark ? "text-white" : "text-slate-900"}>{vehicle.marque} {vehicle.modele}</p>
            </div>
            <div>
              <span className={cn(
                "font-medium",
                isDark ? "text-zinc-400" : "text-slate-600"
              )}>Année:</span>
              <p className={isDark ? "text-white" : "text-slate-900"}>{vehicle.annee}</p>
            </div>
            <div>
              <span className={cn(
                "font-medium",
                isDark ? "text-zinc-400" : "text-slate-600"
              )}>Kilométrage:</span>
              <p className={isDark ? "text-white" : "text-slate-900"}>{vehicle.kilometrage?.toLocaleString()} km</p>
            </div>
            {vehicle.date_assurance && (
              <div>
                <span className={cn(
                  "font-medium",
                  isDark ? "text-zinc-400" : "text-slate-600"
                )}>Assurance:</span>
                <p className={isDark ? "text-white" : "text-slate-900"}>{formatDate(vehicle.date_assurance)}</p>
              </div>
            )}
            {vehicle.date_controle_technique && (
              <div>
                <span className={cn(
                  "font-medium",
                  isDark ? "text-zinc-400" : "text-slate-600"
                )}>Contrôle technique:</span>
                <p className={isDark ? "text-white" : "text-slate-900"}>{formatDate(vehicle.date_controle_technique)}</p>
              </div>
            )}
            {vehicle.date_prochain_entretien && (
              <div>
                <span className={cn(
                  "font-medium",
                  isDark ? "text-zinc-400" : "text-slate-600"
                )}>Prochain entretien:</span>
                <p className={isDark ? "text-white" : "text-slate-900"}>{formatDate(vehicle.date_prochain_entretien)}</p>
              </div>
            )}
          </div>
        </div>

        {/* Documents */}
        <div className={cn(
          "rounded-xl border p-6",
          isDark ? "bg-zinc-900/50 border-white/10" : "bg-white border-slate-200 shadow-sm"
        )}>
          <h3 className={cn(
            "text-lg font-semibold mb-4",
            isDark ? "text-white" : "text-slate-900"
          )}>Documents</h3>
          {vehicle.documents && vehicle.documents.length > 0 ? (
            <div className="space-y-3">
              {vehicle.documents.map((doc) => (
                <div key={doc.id} className={cn(
                  "text-sm border-l-4 border-blue-500 pl-3 py-2",
                  isDark ? "bg-blue-500/10" : ""
                )}>
                  <p className={cn(
                    "font-medium",
                    isDark ? "text-white" : "text-slate-900"
                  )}>{doc.nom}</p>
                  {doc.date_expiration && (
                    <p className={cn(
                      "text-xs mt-1",
                      isDark ? "text-zinc-400" : "text-slate-600"
                    )}>
                      Expire le: {formatDate(doc.date_expiration)}
                    </p>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <p className={isDark ? "text-zinc-500" : "text-slate-500"}>Aucun document</p>
          )}
        </div>

        {/* Dégâts et incidents */}
        <div className={cn(
          "rounded-xl border p-6",
          isDark ? "bg-zinc-900/50 border-white/10" : "bg-white border-slate-200 shadow-sm"
        )}>
          <div className="flex items-center justify-between mb-4">
            <h3 className={cn(
              "text-lg font-semibold",
              isDark ? "text-white" : "text-slate-900"
            )}>Dégâts & Incidents</h3>
            {hasRole(['admin', 'responsable']) && (
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" className="bg-red-500 hover:bg-red-600 text-white" data-testid="add-degat-button">
                    <Plus size={16} className="mr-1" />
                    Ajouter
                  </Button>
                </DialogTrigger>
                <DialogContent className={isDark ? "bg-zinc-900 border-white/10 text-white" : ""}>
                  <DialogHeader>
                    <DialogTitle className={isDark ? "text-white" : ""}>Signaler un dégât</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddDegat} className="space-y-4">
                    <div>
                      <Label htmlFor="description" className={isDark ? "text-zinc-300" : ""}>Description du dégât *</Label>
                      <Textarea
                        id="description"
                        value={newDegat.description}
                        onChange={(e) => setNewDegat({...newDegat, description: e.target.value})}
                        rows={4}
                        placeholder="Décrivez le dégât en détail..."
                        required
                        className={isDark ? "bg-zinc-800 border-white/10 text-white" : ""}
                      />
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="constat"
                        checked={newDegat.constat_fait}
                        onCheckedChange={(checked) => setNewDegat({...newDegat, constat_fait: checked})}
                      />
                      <label
                        htmlFor="constat"
                        className={cn(
                          "text-sm font-medium leading-none",
                          isDark ? "text-zinc-300" : ""
                        )}
                      >
                        Un constat a été établi
                      </label>
                    </div>
                    <div>
                      <Label htmlFor="cout" className={isDark ? "text-zinc-300" : ""}>Coût estimé (€) (optionnel)</Label>
                      <Input
                        id="cout"
                        type="number"
                        step="0.01"
                        value={newDegat.cout_estime}
                        onChange={(e) => setNewDegat({...newDegat, cout_estime: e.target.value})}
                        placeholder="0.00"
                        className={isDark ? "bg-zinc-800 border-white/10 text-white" : ""}
                      />
                    </div>
                    <Button type="submit" className="w-full bg-red-500 hover:bg-red-600 text-white">Enregistrer</Button>
                  </form>
                </DialogContent>
              </Dialog>
            )}
          </div>

          {degatsActifs.length > 0 && (
            <div className="space-y-3 mb-6">
              <h4 className={cn(
                "text-sm font-medium uppercase",
                isDark ? "text-zinc-400" : "text-slate-700"
              )}>En attente de réparation</h4>
              {degatsActifs.map((degat) => (
                <div 
                  key={degat.id} 
                  className={cn(
                    "p-3 border-l-4 border-red-500 rounded-r-lg",
                    isDark ? "bg-red-500/10" : "bg-red-50"
                  )}
                  data-testid={`degat-${degat.id}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <p className={cn(
                        "text-sm font-medium",
                        isDark ? "text-white" : "text-slate-900"
                      )}>{degat.description}</p>
                      <p className={cn(
                        "text-xs mt-1",
                        isDark ? "text-zinc-400" : "text-slate-500"
                      )}>
                        {formatDate(degat.date)} - Par {degat.created_by_nom}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3 mt-3 text-xs">
                    {degat.constat_fait ? (
                      <span className="flex items-center gap-1 text-green-400 bg-green-500/20 px-2 py-1 rounded">
                        <FileText size={14} />
                        Constat fait
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-amber-400 bg-amber-500/20 px-2 py-1 rounded">
                        <AlertCircle size={14} />
                        Pas de constat
                      </span>
                    )}
                    
                    {degat.cout_estime && (
                      <span className={isDark ? "text-zinc-400" : "text-slate-600"}>
                        Coût estimé: {degat.cout_estime.toFixed(2)} €
                      </span>
                    )}
                  </div>

                  {hasRole(['admin', 'responsable']) && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => toggleReparation(degat.id)}
                      className={cn(
                        "mt-3 w-full",
                        isDark ? "bg-zinc-800 border-white/10 text-white hover:bg-zinc-700" : ""
                      )}
                    >
                      <CheckCircle size={14} className="mr-1" />
                      Marquer comme réparé
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}

          {degatsRepares.length > 0 && (
            <div className="space-y-3">
              <h4 className={cn(
                "text-sm font-medium uppercase",
                isDark ? "text-zinc-400" : "text-slate-700"
              )}>Historique réparations</h4>
              {degatsRepares.map((degat) => (
                <div 
                  key={degat.id} 
                  className={cn(
                    "p-3 border-l-4 rounded-r-lg opacity-75",
                    isDark ? "bg-zinc-800/50 border-zinc-600" : "bg-slate-50 border-slate-300"
                  )}
                >
                  <p className={cn(
                    "text-sm",
                    isDark ? "text-zinc-300" : "text-slate-700"
                  )}>{degat.description}</p>
                  <div className={cn(
                    "flex items-center gap-2 mt-2 text-xs",
                    isDark ? "text-zinc-400" : "text-slate-500"
                  )}>
                    <CheckCircle size={14} className="text-green-500" />
                    <span>Réparé le {formatDate(degat.date_reparation)}</span>
                  </div>
                  {degat.constat_fait && (
                    <span className="inline-block mt-2 text-xs text-green-500">
                      • Constat disponible
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}

          {vehicle.degats?.length === 0 && (
            <p className={cn(
              "text-sm text-center py-8",
              isDark ? "text-zinc-500" : "text-slate-500"
            )}>
              Aucun dégât signalé
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
